import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../stores/gameStore';
import { updatePlayerProfile } from '../services/gameService';
import { haptic, sanitizeWebUserName, saveWebUser } from '../services/telegram';
import { playerDisplayName, playerFullName } from '../services/playerProfile';
import { opponentWinRate } from '../services/opponentStats';
import { currencyForUser, formatCurrencyAmount, type EconomyCurrency } from '../services/economy';
import {
  requestWalletHistory,
  type WalletHistoryEntry,
  type WalletHistoryEntryKind,
  type WalletHistoryStatus,
} from '../services/payments';
import type { EconomyTransaction } from '../stores/gameStore';
import { ArrowLeftIcon } from '../components/icons/ArrowLeftIcon';
import { TrophyIcon } from '../components/icons/TrophyIcon';
import { TelegramStarsIcon } from '../components/icons/TelegramStarsIcon';
import { CheckIcon } from '../components/icons/CheckIcon';
import { CrossIcon } from '../components/icons/CrossIcon';
import { ControllerIcon } from '../components/icons/ControllerIcon';
import { CoinsIcon } from '../components/icons/CoinsIcon';
import { FlameIcon } from '../components/icons/FlameIcon';
import { SwordsIcon } from '../components/icons/SwordsIcon';
import { TopUpOverlay } from '../components/topUp/TopUpOverlay';
import { ShieldIcon } from '../components/icons/ShieldIcon';
import { StarIcon } from '../components/icons/StarIcon';
import { ChartIcon } from '../components/icons/ChartIcon';
import { UserIcon } from '../components/icons/UserIcon';

export function ProfileScreen() {
  const {
    telegramUser,
    rating,
    stats,
    seasonPoints,
    opponentStats,
    elmBalance,
    setScreen,
    setTelegramUser,
    transactions,
    walletHistory,
    walletHistorySummary,
    walletHistoryStatus,
    setWalletHistory,
  } = useGameStore();

  const displayName = playerDisplayName(telegramUser);
  const profileSubtitle = telegramUser?.source === 'telegram' && telegramUser.username
    ? playerFullName(telegramUser)
    : telegramUser?.username
      ? `@${telegramUser.username}`
      : '';
  const isWebUser = telegramUser?.source === 'web';
  const isTelegramUser = telegramUser?.source === 'telegram';
  const currency = currencyForUser(telegramUser);
  const [draftName, setDraftName] = useState(displayName);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const cleanDraftName = useMemo(() => sanitizeWebUserName(draftName), [draftName]);
  const canSaveWebName =
    isWebUser && cleanDraftName.length > 0 && cleanDraftName !== displayName && saveStatus !== 'saving';
  const historyItems = useMemo(
    () => buildHistoryItems(walletHistory, transactions),
    [walletHistory, transactions],
  );

  useEffect(() => {
    setDraftName(displayName);
    setSaveStatus('idle');
  }, [displayName]);

  useEffect(() => {
    if (telegramUser?.source !== 'telegram') {
      setWalletHistory([], null, 'idle');
      return;
    }

    const initData = telegramUser.initData ?? '';
    if (!initData) {
      setWalletHistory([], null, 'failed');
      return;
    }

    let cancelled = false;
    setWalletHistory([], null, 'loading');
    void requestWalletHistory({ initData }).then((history) => {
      if (cancelled) return;
      setWalletHistory(history.entries, history.summary, 'ready');
    }).catch(() => {
      if (cancelled) return;
      setWalletHistory([], null, 'failed');
    });

    return () => {
      cancelled = true;
    };
  }, [telegramUser?.id, telegramUser?.initData, telegramUser?.source, setWalletHistory]);

  const winRate =
    stats.wins + stats.losses > 0
      ? Math.round((stats.wins / (stats.wins + stats.losses)) * 100)
      : 0;

  const totalGames = stats.wins + stats.losses;
  const recordDelta = stats.wins - stats.losses;
  const sourceLabel = isTelegramUser ? 'Telegram' : 'Web';
  const accountLabel = isTelegramUser ? 'Telegram account' : 'Browser profile';
  const ratingTier = ratingTierFor(rating);
  const walletStatusLabel = walletStatusText(walletHistoryStatus, isTelegramUser);

  const handleSaveWebName = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!telegramUser || !isWebUser || !cleanDraftName) return;

    setSaveStatus('saving');
    const savedUser = saveWebUser({
      id: telegramUser.id,
      first_name: cleanDraftName,
      photo_url: telegramUser.photo_url,
    });
    const nextUser = { ...savedUser, source: 'web' as const };
    setTelegramUser(nextUser);
    setDraftName(savedUser.first_name);

    try {
      await updatePlayerProfile(nextUser);
      haptic.success();
      setSaveStatus('saved');
    } catch {
      haptic.error();
      setSaveStatus('error');
    }
  };

  return (
    <div className="game-home-stage profile-stage flex h-full flex-col overflow-y-auto scrollbar-hide">
      <div className="profile-shell">
        <motion.header
          className="profile-topbar"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button
            data-nav
            className="hud-icon-button flex items-center justify-center"
            aria-label="Back to home"
            onClick={() => setScreen('home')}
          >
            <ArrowLeftIcon size={18} className="text-text-secondary" />
          </button>

          <div className="min-w-0 flex-1">
            <div className="profile-kicker">Champion dossier</div>
            <h1 className="profile-title">Profile</h1>
          </div>

          <div className="profile-source-pill" aria-label={`${sourceLabel} profile`}>
            <UserIcon size={14} />
            <span>{sourceLabel}</span>
          </div>
        </motion.header>

        <motion.section
          className="profile-hero-panel"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="profile-avatar-ring" aria-hidden="true" />
          <div className="profile-avatar">
            {telegramUser?.photo_url ? (
              <img
                src={telegramUser.photo_url}
                alt={`${displayName} avatar`}
                className="h-full w-full object-cover"
              />
            ) : (
              <span>{displayName.charAt(0).toUpperCase()}</span>
            )}
          </div>

          <div className="profile-hero-copy">
            <div className="profile-kicker">Arena identity</div>
            <h2 className="profile-player-name">{displayName}</h2>
            <div className="profile-player-subtitle">
              {profileSubtitle || accountLabel}
            </div>
            <div className="profile-badge-row">
              <span className="profile-badge profile-badge-gold">
                <TrophyIcon size={15} />
                {rating.toLocaleString()} rating
              </span>
              <span className="profile-badge">
                <ShieldIcon size={15} />
                {ratingTier}
              </span>
            </div>
          </div>
        </motion.section>

        {isWebUser && (
          <motion.form
            className="profile-name-forge"
            onSubmit={handleSaveWebName}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04 }}
          >
            <label htmlFor="profile-web-username" className="arena-section-label">
              Web username
            </label>
            <div className="profile-name-control">
              <input
                id="profile-web-username"
                data-nav
                aria-label="Web username"
                className="profile-name-input"
                value={draftName}
                maxLength={32}
                onChange={(event) => {
                  setDraftName(event.target.value);
                  if (saveStatus !== 'idle') setSaveStatus('idle');
                }}
              />
              <motion.button
                data-nav
                type="submit"
                className="profile-save-button"
                disabled={!canSaveWebName}
                whileTap={canSaveWebName ? { scale: 0.95 } : undefined}
                aria-label="Save web username"
              >
                <CheckIcon size={18} className={canSaveWebName ? 'text-energy-high' : 'text-text-muted'} />
              </motion.button>
            </div>
            {saveStatus !== 'idle' && (
              <div
                role="status"
                className={`profile-save-status ${
                  saveStatus === 'error'
                    ? 'text-energy-low'
                    : saveStatus === 'saved'
                      ? 'text-energy-high'
                      : 'text-text-secondary'
                }`}
              >
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save failed'}
              </div>
            )}
          </motion.form>
        )}

        <motion.section
          className="profile-treasury"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06, duration: 0.2 }}
        >
          <div className="profile-panel-heading">
            <div>
              <div className="arena-section-label">Treasury</div>
              <h2>Match credits</h2>
            </div>
            <span className="profile-currency-chip">{currency}</span>
          </div>

          <div className="profile-balance-row">
            <div className="min-w-0">
              <div className="profile-balance-number balance-number-inline glow-text-gold tabular-nums">
                {elmBalance.toLocaleString()}
              </div>
              <div className="profile-note">
                {isTelegramUser ? 'Server-synced ELM balance' : 'Demo balance for local testing'}
              </div>
            </div>
            <TopUpOverlay />
          </div>

          <div className="profile-mini-ledger" aria-label="Progress summary">
            <div>
              <StarIcon size={16} className="text-gold" />
              <strong>{seasonPoints.toLocaleString()}</strong>
              <span>Season Points</span>
            </div>
            <div>
              <ChartIcon size={16} className="text-water-light" />
              <strong>{recordDelta >= 0 ? `+${recordDelta}` : recordDelta}</strong>
              <span>Record edge</span>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="profile-section"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.2 }}
        >
          <div className="profile-panel-heading">
            <div>
              <div className="arena-section-label">Combat record</div>
              <h2>Battle rhythm</h2>
            </div>
            <span className={`profile-record-chip ${recordDelta >= 0 ? 'profile-record-chip-good' : 'profile-record-chip-danger'}`}>
              {recordDelta >= 0 ? `+${recordDelta}` : recordDelta}
            </span>
          </div>

          <div className="profile-stat-grid">
            <ProfileStat
              label="Wins"
              value={stats.wins.toLocaleString()}
              tone="success"
              icon={<CheckIcon size={20} />}
            />
            <ProfileStat
              label="Losses"
              value={stats.losses.toLocaleString()}
              tone="danger"
              icon={<CrossIcon size={20} />}
            />
            <ProfileStat
              label="Games"
              value={totalGames.toLocaleString()}
              tone="water"
              icon={<ControllerIcon size={20} />}
            />
            <ProfileStat
              label="Win rate"
              value={`${winRate}%`}
              tone={winRate >= 60 ? 'success' : winRate >= 40 ? 'gold' : 'danger'}
              icon={<ChartIcon size={20} />}
            />
          </div>

          <div className="profile-winrate">
            <div className="profile-winrate-head">
              <span>Win Rate</span>
              <strong>{winRate}%</strong>
            </div>
            <div className="profile-winrate-track">
              <motion.div
                className={`profile-winrate-fill ${winRateFillClass(winRate)}`}
                initial={{ width: 0 }}
                animate={{ width: `${winRate}%` }}
                transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>
        </motion.section>

        <motion.section
          className="profile-section"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14, duration: 0.2 }}
        >
          <div className="profile-panel-heading">
            <div>
              <div className="arena-section-label">Rivals</div>
              <h2>Head to head</h2>
            </div>
            <span className="profile-count-pill">{opponentStats.length}</span>
          </div>

          {opponentStats.length === 0 ? (
            <ProfileEmptyState
              icon={<SwordsIcon size={30} />}
              title="No rivals yet"
              text="Finish a match to track opponent records."
            />
          ) : (
            <div className="profile-rival-list">
              {opponentStats.slice(0, 8).map((rival) => {
                const rivalWinRate = opponentWinRate(rival);
                return (
                  <div key={rival.opponentName} className="profile-rival-row">
                    <div className="profile-rival-main">
                      <div className="profile-rival-name">{rival.opponentName}</div>
                      <div className="profile-rival-meta">
                        {rival.matches} games, {rivalWinRate}% win rate
                      </div>
                      <div className="profile-rival-track">
                        <motion.div
                          className="profile-rival-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${rivalWinRate}%` }}
                          transition={{ duration: 0.65, delay: 0.12 }}
                        />
                      </div>
                    </div>
                    <div className="profile-rival-score" aria-label={`Record against ${rival.opponentName}`}>
                      <span className="text-energy-high">{rival.wins}</span>
                      <span className="text-text-muted">/</span>
                      <span className="text-energy-low">{rival.losses}</span>
                      <span className="text-text-secondary">{rival.draws}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.section>

        <motion.section
          className="profile-section profile-wallet-section"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.2 }}
        >
          <div className="profile-panel-heading">
            <div>
              <div className="arena-section-label">Wallet History</div>
              <h2>Activity ledger</h2>
            </div>
            {walletStatusLabel ? (
              <span className={`profile-sync-pill ${walletHistoryStatus === 'failed' ? 'profile-sync-pill-danger' : ''}`}>
                {walletStatusLabel}
              </span>
            ) : null}
          </div>

          {walletHistorySummary ? (
            <div className="profile-wallet-summary">
              <SummaryPill label="Stars in" value={walletHistorySummary.totalStarsPurchased.toLocaleString()} />
              <SummaryPill label={`${currency} credited`} value={walletHistorySummary.totalElmCredited.toLocaleString()} />
              <SummaryPill
                label="PvP net"
                value={formatCurrencyAmount(walletHistorySummary.pvpNetElm, currency, { signed: true })}
              />
            </div>
          ) : null}

          {historyItems.length === 0 ? (
            <ProfileEmptyState
              icon={<CoinsIcon size={30} />}
              title="No wallet activity yet"
              text="Matches and top-ups will appear here."
            />
          ) : (
            <div className="profile-history-list">
              {historyItems.slice(0, 30).map((item) => (
                <div key={item.id} className="profile-history-row">
                  <div className="profile-history-icon" aria-hidden="true">
                    {historyIcon(item)}
                  </div>
                  <div className="profile-history-main">
                    <div className="profile-history-title-row">
                      <span>{item.title}</span>
                      <span className={`profile-history-status ${historyStatusClass(item.status)}`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="profile-history-description">{item.description}</div>
                    <div className="profile-history-time">{formatHistoryTime(item.occurredAt)}</div>
                  </div>
                  <div className={`profile-history-amount ${historyAmountTone(item)}`}>
                    {historyAmountLabel(item, currency)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.section>

        {totalGames === 0 && (
          <motion.section
            className="profile-first-match"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22, duration: 0.2 }}
          >
            <SwordsIcon size={24} className="text-gold" />
            <div>
              <div className="profile-first-match-title">Ready for battle?</div>
              <div className="profile-note">Play your first match to open the record.</div>
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
}

type ProfileStatTone = 'success' | 'danger' | 'water' | 'gold';

function ProfileStat({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: string;
  tone: ProfileStatTone;
  icon: React.ReactNode;
}) {
  return (
    <div className={`profile-stat profile-stat-${tone}`}>
      <div className="profile-stat-icon">{icon}</div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function ProfileEmptyState({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="profile-empty-state">
      <div className="profile-empty-icon">{icon}</div>
      <strong>{title}</strong>
      <span>{text}</span>
    </div>
  );
}

function SummaryPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="profile-summary-pill">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ratingTierFor(rating: number): string {
  if (rating >= 1900) return 'Mythic rank';
  if (rating >= 1600) return 'Ascendant rank';
  if (rating >= 1350) return 'Adept rank';
  return 'Initiate rank';
}

function walletStatusText(status: 'idle' | 'loading' | 'ready' | 'failed', isTelegramUser: boolean): string {
  if (!isTelegramUser) return 'Demo';
  if (status === 'loading') return 'Syncing';
  if (status === 'failed') return 'Offline';
  if (status === 'ready') return 'Synced';
  return '';
}

function winRateFillClass(winRate: number): string {
  if (winRate >= 60) return 'profile-winrate-fill-success';
  if (winRate >= 40) return 'profile-winrate-fill-gold';
  return 'profile-winrate-fill-danger';
}

interface HistoryDisplayItem {
  id: string;
  kind: WalletHistoryEntryKind | EconomyTransaction['type'];
  status: WalletHistoryStatus;
  title: string;
  description: string;
  occurredAt: string;
  elmAmount: number;
  starsAmount?: number;
  matchId?: string;
}

function buildHistoryItems(
  walletHistory: WalletHistoryEntry[],
  transactions: EconomyTransaction[],
): HistoryDisplayItem[] {
  const walletMatchIds = new Set(
    walletHistory
      .filter(entry => entry.matchId && isMatchWalletKind(entry.kind))
      .map(entry => entry.matchId),
  );
  const localItems = transactions
    .filter(tx => !walletMatchIds.has(tx.matchId))
    .map(transactionToHistoryItem);

  return [
    ...walletHistory.map(walletEntryToHistoryItem),
    ...localItems,
  ].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

function walletEntryToHistoryItem(entry: WalletHistoryEntry): HistoryDisplayItem {
  return {
    id: entry.id,
    kind: entry.kind,
    status: entry.status,
    title: entry.title,
    description: entry.description,
    occurredAt: entry.occurredAt,
    elmAmount: entry.elmAmount,
    starsAmount: entry.starsAmount,
    matchId: entry.matchId,
  };
}

function transactionToHistoryItem(tx: EconomyTransaction): HistoryDisplayItem {
  return {
    id: tx.id,
    kind: tx.type,
    status: 'settled',
    title: transactionTitle(tx.type),
    description: tx.description,
    occurredAt: new Date(tx.timestamp).toISOString(),
    elmAmount: tx.amount,
    matchId: tx.matchId,
  };
}

function transactionTitle(type: EconomyTransaction['type']): string {
  switch (type) {
    case 'entry_fee':
      return 'Match entry fee';
    case 'boost_cost':
      return 'Energy Boost cost';
    case 'stake':
      return 'Legacy match debit';
    case 'win':
      return 'PvP result';
    case 'loss':
      return 'PvP loss';
    case 'boost_burn':
      return 'Energy Boost spent';
    case 'boost_return':
      return 'Energy Boost returned';
    case 'rake':
      return 'Legacy match fee';
  }
}

function historyIcon(item: HistoryDisplayItem): React.ReactNode {
  if (item.kind === 'stars_purchase') return <TelegramStarsIcon size={16} className="text-gold" />;
  if (item.kind === 'stars_refund') return <TelegramStarsIcon size={16} className={item.status === 'pending' ? 'text-text-muted' : 'text-energy-high'} />;
  if (item.kind === 'elm_credit' || item.kind === 'pvp_win' || item.kind === 'pvp_draw_refund') {
    return <CoinsIcon size={16} className="text-energy-high" />;
  }
  if (item.kind === 'match_boost_cost' || item.kind === 'pvp_boost_stake' || item.kind === 'boost_cost' || item.kind === 'boost_burn') return <FlameIcon size={16} className="text-energy-low" />;
  if (item.kind === 'pvp_boost_return' || item.kind === 'boost_return') return <CheckIcon size={16} className="text-energy-high" />;
  if (item.elmAmount < 0) return <CrossIcon size={16} className="text-energy-low" />;
  return <CoinsIcon size={16} className="text-text-muted" />;
}

function isMatchWalletKind(kind: WalletHistoryEntryKind): boolean {
  return kind.startsWith('pvp_') || kind.startsWith('match_');
}

function historyStatusClass(status: WalletHistoryStatus): string {
  switch (status) {
    case 'settled':
      return 'text-energy-high';
    case 'pending':
      return 'text-gold';
    case 'failed':
      return 'text-energy-low';
  }
}

function historyAmountTone(item: HistoryDisplayItem): string {
  if (item.kind === 'stars_purchase') return 'profile-history-amount-gold';
  if (item.kind === 'stars_refund' || item.elmAmount > 0) return 'profile-history-amount-good';
  if (item.elmAmount < 0) return 'profile-history-amount-danger';
  return 'profile-history-amount-muted';
}

function historyAmountLabel(item: HistoryDisplayItem, currency: EconomyCurrency): string {
  if (item.kind === 'stars_purchase') return `${item.starsAmount ?? 0} Stars`;
  if (item.kind === 'stars_refund') return `+${item.starsAmount ?? 0} Stars`;
  if (item.elmAmount === 0) return 'n/a';
  return formatCurrencyAmount(item.elmAmount, currency, { signed: true });
}

function formatHistoryTime(value: string): string {
  const time = new Date(value);
  if (Number.isNaN(time.getTime())) return '';
  return time.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
